import axios from 'axios';

const appBasePath = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
const apiBaseURL = `${appBasePath === '' ? '' : appBasePath}/api`;

const api = axios.create({
  baseURL: apiBaseURL,
  withCredentials: true,
});

function readPassiveHeader(headers: unknown): string {
  if (!headers) return '';

  const maybeAxiosHeaders = headers as { get?: (name: string) => string | undefined | null };
  if (typeof maybeAxiosHeaders.get === 'function') {
    return (
      maybeAxiosHeaders.get('X-Passive-Request') ||
      maybeAxiosHeaders.get('x-passive-request') ||
      ''
    );
  }

  const recordHeaders = headers as Record<string, unknown>;
  return String(
    recordHeaders['X-Passive-Request'] ??
      recordHeaders['x-passive-request'] ??
      ''
  );
}

api.interceptors.request.use((config) => {
  const passiveHeader = readPassiveHeader(config.headers);
  const isPassiveRequest = String(passiveHeader).toLowerCase() === 'true';

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('api:request', {
        detail: {
          passive: isPassiveRequest,
          url: config.url || '',
          method: String(config.method || 'get').toUpperCase(),
        },
      }),
    );
  }

  return config;
});

api.interceptors.response.use(
  (response) => {
    const passiveHeader = readPassiveHeader(response.config?.headers);
    const isPassiveRequest = String(passiveHeader).toLowerCase() === 'true';

    if (typeof window !== 'undefined' && !isPassiveRequest) {
      window.dispatchEvent(new CustomEvent('api:activity'));
    }
    return response;
  },
  (error) => Promise.reject(error),
);

/**
 * Fetches the CSRF token from the server and sets it as a default header
 * for all subsequent API requests. This is crucial for the CSRF protection
 * to work correctly.
 */
export const initializeCsrf = async () => {
  try {
    const { data } = await api.get('/csrf-token');
    if (data.token) {
      // Backend (csrf-sync) expects 'x-csrf-token' (lowercase usually works, but consistency is key)
      api.defaults.headers.common['x-csrf-token'] = data.token;
      console.log('CSRF token initialized successfully.');
    }
  } catch (error) {
    console.error('Failed to initialize CSRF token:', error);
  }
};

export default api;
